import React, { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import {
  CONTACT,
  IMAGES,
  LOGO,
  LOGO_DARK,
  PROMO_VIDEO_ID,
  SOCIALS,
  STUDIO,
  SERVICES,
} from "../mock";
import * as Icons from "lucide-react";

/* Reveal-on-scroll wrapper */
export const Reveal = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`reveal ${visible ? "is-visible" : ""} ${className}`}
    >
      {children}
    </div>
  );
};

/* Logo wordmark (official logo image) */
export const Wordmark = ({ size = "w-[320px] md:w-[460px]" }) => (
  <img src={LOGO} alt="Davide Lepido - Sound Engineer" className={`${size} h-auto`} />
);

export const SectionTitle = ({ kicker, title, dark = false }) => (
  <div className="text-center mb-14">
    {kicker && (
      <div className="eyebrow text-xs text-[var(--accent-pink)] font-semibold mb-3">{kicker}</div>
    )}
    <h2 className={`text-4xl md:text-5xl font-semibold tracking-tight ${dark ? "text-white" : "text-[var(--ink)]"}`}>
      {title}
    </h2>
    <div className="w-16 h-[3px] bg-[var(--accent-pink)] mx-auto mt-5" />
  </div>
);

/* ---------------- HERO ---------------- */
export const Hero = () => (
  <section id="home" className="section relative h-screen min-h-[640px] w-full overflow-hidden">
    <img
      src={IMAGES.heroGuitar}
      alt="Sound engineering"
      className="absolute inset-0 w-full h-full object-cover grayscale"
    />
    <div className="absolute inset-0 hero-overlay" />
    <div className="relative h-full flex flex-col items-center justify-center px-6">
      <Reveal>
        <Wordmark />
      </Reveal>
      <button
        onClick={() => document.getElementById("social")?.scrollIntoView({ behavior: "smooth" })}
        className="absolute bottom-10 w-16 h-16 rounded-full border-2 border-white/90 flex items-center justify-center text-white animate-floatY hover:bg-white hover:text-black transition-colors"
        aria-label="Scorri"
      >
        <ChevronDown size={30} />
      </button>
    </div>
  </section>
);

/* ---------------- PROMO VIDEO ---------------- */
export const Promo = () => (
  <section id="promo" className="section bg-white py-24 px-6">
    <div className="max-w-4xl mx-auto">
      <Reveal className="flex justify-center">
        <div className="torn-frame w-full max-w-3xl">
          <div className="aspect-video w-full bg-black">
            <iframe
              className="w-full h-full"
              src={`https://www.youtube.com/embed/${PROMO_VIDEO_ID}`}
              title="Davide Lepido Promo"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      </Reveal>
      <Reveal className="mt-16 flex justify-center" delay={100}>
        <div className="bg-white px-10 py-6 w-full max-w-2xl flex justify-center">
          <img src={LOGO_DARK} alt="Davide Lepido - Sound Engineer" className="w-[300px] md:w-[440px] h-auto" />
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------------- SOCIAL ---------------- */
export const Social = () => (
  <section id="social" className="section bg-[#141414] py-24 px-6">
    <div className="max-w-5xl mx-auto">
      <SectionTitle kicker="Seguimi" title="Social" dark />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-2xl mx-auto">
        {SOCIALS.map((s, i) => {
          const Ico = Icons[s.icon] || Icons.Link;
          return (
            <Reveal key={s.name} delay={i * 80}>
              <a
                href={s.url}
                target="_blank"
                rel="noreferrer"
                className="group flex flex-col items-center justify-center gap-3 py-10 border border-white/10 hover:border-[var(--accent-pink)] bg-white/[0.02] hover:bg-white/[0.05] transition-all duration-300"
              >
                <Ico className="text-white group-hover:text-[var(--accent-pink)] transition-colors" size={38} strokeWidth={1.4} />
                <div className="text-white font-medium tracking-wide">{s.name}</div>
                <div className="text-gray-400 text-sm">{s.handle}</div>
              </a>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

/* ---------------- STUDIO ---------------- */
export const Studio = () => (
  <section id="studio" className="section bg-white py-24 px-6">
    <div className="max-w-6xl mx-auto">
      <SectionTitle kicker="Il mio spazio" title="Studio" />
      <Reveal className="max-w-2xl mx-auto text-center text-gray-600 text-lg font-light mb-14">
        {STUDIO.intro}
      </Reveal>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {STUDIO.gallery.map((g, i) => (
          <Reveal key={i} delay={i * 80} className="img-zoom relative group">
            <div className="aspect-[3/4] w-full overflow-hidden bg-black">
              <img src={IMAGES[g.src]} alt="" className="w-full h-full object-cover grayscale" />
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- SERVICES ---------------- */
export const Services = () => (
  <section id="services" className="section relative py-28 px-6 overflow-hidden">
    <img src={IMAGES.console2} alt="" className="absolute inset-0 w-full h-full object-cover grayscale" />
    <div className="absolute inset-0 bg-black/80" />
    <div className="relative max-w-6xl mx-auto">
      <SectionTitle kicker="Cosa faccio" title="Services" dark />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {SERVICES.map((s, i) => {
          const Ico = Icons[s.icon] || Icons.Music;
          return (
            <Reveal key={s.title} delay={i * 70}>
              <div className="h-full p-8 bg-white/[0.04] border border-white/10 hover:border-[var(--accent-pink)] hover:-translate-y-1 transition-all duration-300">
                <Ico className="text-[var(--accent-pink)] mb-5" size={40} strokeWidth={1.4} />
                <h3 className="text-white text-xl font-semibold tracking-wide mb-2">{s.title}</h3>
                <p className="text-gray-400 font-light leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);
